import React, { ReactNode } from "react";
import Public from "../../_app/layouts/Public";
import { createStylesheet } from "../../_app/utils/styles";
import { useHeadingText, useLoginBackground, useSubHeadingText } from "../../brand/hooks";
import { UILoader, UITypography } from "../../_app/components";
import AuthHeader from "../components/cognito/AuthHeader";
import AuthFooter from "../components/cognito/AuthFooter";
import bgPattern from "../../_app/images/bg-pattern.svg";

interface AuthLayoutProps extends React.HtmlHTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  title: string;
  className?: string;
  isLoading?: boolean;
  banner?: ReactNode;
}

const AuthLayout = ({ children, title, isLoading, className, banner, ...props }: AuthLayoutProps) => {
  const classes = useStyles();
  const loginBg = useLoginBackground();
  const headingText = useHeadingText();
  const subHeadingText = useSubHeadingText();

  if (isLoading) return <UILoader />;
  return (
    <Public title={title} className={`${classes.ctr} ${className}`} {...props}>
      <div className={classes.left} style={{ backgroundImage: `url("${loginBg}"), url("${bgPattern}")` }}>
        <UITypography variant="h1">{headingText}</UITypography>
        <UITypography variant="h3">{subHeadingText}</UITypography>
      </div>
      <div className={classes.right}>
        <AuthHeader />
        {banner ? <div className={classes.banner}>{banner}</div> : null}
        <div className={classes.content}>{children}</div>
        <AuthFooter />
      </div>
    </Public>
  );
};

const useStyles = createStylesheet((theme) => ({
  left: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(8),
    color: theme.palette.common.white,
    background: theme.palette.tertiary.dark,
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    // In the side-by-side layout, pin the hero to the viewport and centre its text there, so its
    // position is independent of the right-hand form height (sign-in vs forgot-password etc.).
    [theme.breakpoints.up("md")]: {
      position: "sticky",
      top: 0,
      height: "100vh",
    },
    // Below the side-by-side layout the hero renders as an awkwardly cropped strip — hide it
    // and let the form take the full width instead.
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },
  right: {
    display: "flex",
    flexDirection: "column",
    flex: 0.9,
    backgroundColor: theme.palette.background.paper,
    minWidth: 500,
    [theme.breakpoints.down("sm")]: {
      minWidth: 250,
    },
  },
  banner: {
    display: "flex",
    justifyContent: "center",
    padding: `0 ${theme.spacing(3)}`,
    "& > *": {
      width: 480,
      maxWidth: "100%",
    },
  },
  content: {
    flex: 1,
    minHeight: 320,
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    paddingTop: theme.spacing(3),
    "& > :first-of-type": {
      width: 480,
      maxWidth: 480,
    },
  },
  ctr: {
    display: "flex",
    flexDirection: "row",
    flex: 1,

    [theme.breakpoints.down("md")]: {
      "&": {
        flexDirection: "column",
      },
    },

    "& [data-amplify-authenticator]": {
      fontFamily: theme.typography.fontFamily,
      backgroundColor: theme.palette.background.paper,
      borderRadius: 6,
      border: "none",
    },
    "& [data-amplify-router]": {
      backgroundColor: theme.palette.background.paper,
      boxShadow: "none",
      border: "none",
    },
    "& [data-amplify-form]": {
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
    "& [data-amplify-authenticator] [data-amplify-footer]": {
      paddingBottom: 0,
    },
    "& .amplify-alert": {
      overflowWrap: "anywhere",
      borderRadius: theme.shape.borderRadius,
    },
    "& .amplify-heading": {
      placeSelf: "center",
      fontSize: "1.25rem",
      fontWeight: 600,
      marginBottom: theme.spacing(2),
    },
    "& [data-amplify-authenticator-confirmsignin] .amplify-label": {
      textAlign: "center",
      paddingBottom: "1rem",
    },
    "& [data-amplify-form] .amplify-button[type='submit']": {
      marginTop: theme.spacing(1.5),
    },
    "& [data-amplify-form] .amplify-button--link": {
      fontWeight: 600,
    },
  },
}));

export default AuthLayout;
